import { useCallback, useRef } from 'react';

/**
 * A generic debounced "save the freshest value" mechanism.
 *
 * Extracted verbatim from the Call Script's hand-rolled output/heard persistence so
 * the two near-identical save paths share one implementation. The semantics are
 * load-bearing for HIPAA data-loss avoidance — read carefully before changing:
 *
 *  - `schedule(v)` records `v` as the pending value and (re)arms the timer. When
 *    the timer fires it runs the persist with whatever is pending at that moment
 *    (so rapid edits coalesce into a single POST of the latest value).
 *  - On a SUCCESSFUL persist the pending value is cleared ONLY if it has not been
 *    superseded by a newer `schedule` in the meantime (identity check). This keeps
 *    an idle unmount/tab-switch from re-POSTing already-saved data while never
 *    dropping an edit that landed mid-flight.
 *  - On a FAILED persist the pending value is KEPT. The runner swallows the throw
 *    (no unhandled rejection) but does NOT null pending on the failure path —
 *    that is what keeps the `beforeunload` guard active after a failed save.
 */
export interface DebouncedSave<T> {
  /** Set pending = value and (re)arm the debounce timer. */
  schedule: (value: T) => void;
  /** Clear the timer and persist the freshest pending value now. Returns the run
   *  promise (resolves even on failure) so callers can chain after it. No-op
   *  (returns undefined) when nothing is pending. */
  flush: () => Promise<void> | void;
  /** The current pending value (freshest), or null when nothing is pending. */
  peek: () => T | null;
  /** Whether a value is currently pending. */
  hasPending: () => boolean;
}

export function useDebouncedSave<T>(
  delayMs: number,
  persist: (value: T) => Promise<void>,
): DebouncedSave<T> {
  // Freshest pending value (null when nothing waiting to be saved).
  const pendingRef = useRef<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep the persist callback fresh each render so the returned methods stay
  // stable (deps below) without capturing a stale closure.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  // The internal runner: persist `v`, clear pending only on success AND only when
  // it wasn't superseded by a newer schedule (identity check). On failure keep
  // pending and swallow the throw (so there's no unhandled rejection).
  const run = useCallback(async (v: T): Promise<void> => {
    try {
      await persistRef.current(v);
      if (pendingRef.current === v) pendingRef.current = null;
    } catch {
      /* keep pending — failure must not lose captured data */
    }
  }, []);

  const schedule = useCallback(
    (value: T) => {
      pendingRef.current = value;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (pendingRef.current != null) void run(pendingRef.current);
      }, delayMs);
    },
    [delayMs, run],
  );

  const flush = useCallback((): Promise<void> | void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const p = pendingRef.current;
    if (p == null) return;
    return run(p);
  }, [run]);

  const peek = useCallback((): T | null => pendingRef.current, []);
  const hasPending = useCallback((): boolean => pendingRef.current != null, []);

  return { schedule, flush, peek, hasPending };
}
